/**
 * Optimistic patch of /budgets/overview/ caches when an expense is created/updated/deleted.
 * Keeps Despesas hero + «Onde está a ir» in sync before refetch.
 */

import { toNumber } from './number';

function formatMoney(value) {
  return value.toFixed(2);
}

function matchesCategory(row, categoryId) {
  if (!categoryId || !row) return false;
  return (
    String(row.category_id ?? '') === String(categoryId) ||
    String(row.id ?? '') === String(categoryId)
  );
}

/**
 * @param {object|null|undefined} overview
 * @param {{ categoryId: string, amountDelta: number }} params
 * @returns {object|null|undefined}
 */
export function applyExpenseDeltaToOverview(overview, { categoryId, amountDelta }) {
  if (!overview || !categoryId || !Number.isFinite(amountDelta) || amountDelta === 0) {
    return overview;
  }

  const groups = Array.isArray(overview.groups) ? overview.groups : null;
  if (!groups) return overview;

  let matched = false;
  const nextGroups = groups.map((group) => {
    const categories = Array.isArray(group.categories) ? group.categories : [];
    const catIndex = categories.findIndex((row) => matchesCategory(row, categoryId));
    if (catIndex < 0) return group;

    matched = true;
    const category = categories[catIndex];
    const nextSpent = Math.max(0, toNumber(category.spent_eur) + amountDelta);
    const budget = category.budget_eur != null ? toNumber(category.budget_eur) : null;
    const spentPct =
      budget != null && budget > 0 ? Math.round((nextSpent / budget) * 10000) / 100 : category.spent_pct;

    const nextCategories = categories.map((row, index) =>
      index === catIndex
        ? {
            ...row,
            spent_eur: formatMoney(nextSpent),
            spent_pct: spentPct,
          }
        : row,
    );

    const groupSpent = Math.max(0, toNumber(group.spent_eur) + amountDelta);
    const isReserved = Boolean(group.is_pay_yourself_first);
    const reserved = toNumber(group.reserved_eur ?? group.target_budget_eur ?? group.budget_eur);
    const remaining = isReserved
      ? Math.max(0, reserved - groupSpent)
      : group.budget_eur != null
        ? Math.max(0, toNumber(group.budget_eur) - groupSpent)
        : group.remaining_eur;

    return {
      ...group,
      categories: nextCategories,
      spent_eur: formatMoney(groupSpent),
      remaining_eur:
        remaining == null ? group.remaining_eur : formatMoney(remaining),
      // Envelope PYF continua «cheio»; gasto real actualiza spent_eur acima.
      spent_pct: isReserved ? '100.00' : group.spent_pct,
    };
  });

  if (!matched) return overview;

  const totalSpent = Math.max(0, toNumber(overview.total_spent_eur) + amountDelta);
  const totalBudget = toNumber(overview.total_budget_eur);
  const totalSpentPct =
    totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 10000) / 100 : overview.total_spent_pct;

  return {
    ...overview,
    groups: nextGroups,
    total_spent_eur: formatMoney(totalSpent),
    total_spent_pct: totalSpentPct,
  };
}

/**
 * @param {import('@tanstack/react-query').QueryClient} qc
 * @param {{ categoryId: string, amountDelta: number }} params
 * @returns {Array<[unknown, unknown]>} previous overview entries for rollback
 */
export function patchBudgetOverviewSpend(qc, { categoryId, amountDelta }) {
  const previous = qc.getQueriesData({ queryKey: ['budgets', 'overview'], exact: false });
  qc.setQueriesData({ queryKey: ['budgets', 'overview'], exact: false }, (old) =>
    applyExpenseDeltaToOverview(old, { categoryId, amountDelta }),
  );
  return previous;
}
